import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { 
  User, Mail, Phone, Lock, Save, Plus, Trash2, Shield, 
  CheckCircle, AlertCircle, Eye, EyeOff 
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProfileSettings = () => {
  const { user, token, updateUser } = useAuth();
  const { t } = useLanguage();
  
  const [loading, setLoading] = useState(false);
  const [profileData, setProfileData] = useState({
    full_name: '',
    email: '',
    mobile_number: '',
    additional_emails: []
  });
  
  const [passwordData, setPasswordData] = useState(() => ({
    // Prefill Current Password from the sessionStorage hint we set on
    // login (and refresh on successful change). This is what makes the
    // field auto-populate even when Chrome's password manager didn't
    // update — no browser cooperation required. Hint is per-tab and
    // cleared on logout. See AuthContext.login for the security note.
    current_password: sessionStorage.getItem('pw_hint') || '',
    new_password: '',
    confirm_password: ''
  }));
  
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  
  const [newAdditionalEmail, setNewAdditionalEmail] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [hasPassword, setHasPassword] = useState(true); // Assume true initially

  useEffect(() => {
    if (user) {
      fetchUserProfile();
    }
  }, [user, token]);

  const fetchUserProfile = async () => {
    try {
      const response = await axios.get(`${API}/users/profile?token=${token}`);
      if (response.data) {
        setProfileData({
          full_name: response.data.full_name || '',
          email: response.data.email || '',
          mobile_number: response.data.mobile_number || '',
          additional_emails: response.data.additional_emails || []
        });
        // Check if user has a password set
        setHasPassword(response.data.has_password !== false);
      }
    } catch (error) {
      console.error('Failed to fetch profile:', error);
      // Fallback to user context data
      setProfileData({
        full_name: user?.full_name || '',
        email: user?.email || '',
        mobile_number: user?.mobile_number || '',
        additional_emails: user?.additional_emails || []
      });
    }
  };

  const handleSaveProfile = async () => {
    setLoading(true);
    try {
      const response = await axios.put(`${API}/users/profile?token=${token}`, {
        full_name: profileData.full_name,
        mobile_number: profileData.mobile_number,
        additional_emails: profileData.additional_emails
      });
      
      if (response.data.success) {
        toast.success('Profile updated successfully');
        if (updateUser) {
          updateUser(response.data.user);
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (passwordData.new_password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    setPasswordLoading(true);
    try {
      // If user doesn't have a password, use set-password endpoint
      if (!hasPassword) {
        const response = await axios.post(`${API}/users/set-password?token=${token}`, {
          new_password: passwordData.new_password,
          confirm_password: passwordData.confirm_password
        });
        
        if (response.data.success) {
          toast.success('Password set successfully! You can now use email/password to login.');
          setHasPassword(true);
          sessionStorage.setItem('pw_hint', passwordData.new_password);
          setPasswordData({
            current_password: passwordData.new_password,
            new_password: '',
            confirm_password: ''
          });
        }
      } else {
        // User has password, use change-password endpoint
        const response = await axios.post(`${API}/users/change-password?token=${token}`, {
          current_password: passwordData.current_password,
          new_password: passwordData.new_password
        });

        if (response.data.success) {
          toast.success('Password changed successfully');
          // Refresh the sessionStorage prefill hint so the next open of
          // this page (still within the same tab) shows the NEW password
          // as Current Password, not the one we just replaced.
          sessionStorage.setItem('pw_hint', passwordData.new_password);
          // Reset visible fields; the new "current" fills back in from
          // the hint on next mount / navigation.
          setPasswordData({
            current_password: passwordData.new_password,
            new_password: '',
            confirm_password: ''
          });
        }
      }
    } catch (error) {
      const errorDetail = error.response?.data?.detail || 'Failed to change password';
      
      // If error says password not set, switch to set password mode
      if (errorDetail.includes('not set') || errorDetail.includes('OAuth')) {
        setHasPassword(false);
        toast.info('Please set a new password for your account.');
      } else {
        toast.error(errorDetail);
      }
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleAddEmail = () => {
    if (!newAdditionalEmail.trim()) {
      toast.error('Please enter an email address');
      return;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newAdditionalEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    if (profileData.additional_emails.includes(newAdditionalEmail)) {
      toast.error('This email is already added');
      return;
    }
    
    if (newAdditionalEmail === profileData.email) {
      toast.error('This is already your primary email');
      return;
    }
    
    setProfileData(prev => ({
      ...prev,
      additional_emails: [...prev.additional_emails, newAdditionalEmail]
    }));
    setNewAdditionalEmail('');
    toast.success('Email added. Click Save to confirm.');
  };

  const handleRemoveEmail = (emailToRemove) => {
    setProfileData(prev => ({
      ...prev,
      additional_emails: prev.additional_emails.filter(email => email !== emailToRemove)
    }));
    toast.success('Email removed. Click Save to confirm.');
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6" data-testid="profile-settings-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Profile Settings</h1>
          <p className="text-muted-foreground">Manage your account information and security</p>
        </div>
        <Badge variant="outline" className="flex items-center gap-1">
          <Shield size={14} />
          {user?.role || 'User'}
        </Badge>
      </div>

      {/* Basic Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User size={20} />
            Basic Information
          </CardTitle>
          <CardDescription>Update your personal details</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="full_name">Full Name</Label>
              <Input
                id="full_name"
                data-testid="profile-full-name"
                value={profileData.full_name}
                onChange={(e) => setProfileData(prev => ({ ...prev, full_name: e.target.value }))}
                placeholder="Enter your full name"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email">Primary Email</Label>
              <Input
                id="email"
                data-testid="profile-email"
                value={profileData.email}
                disabled
                className="bg-muted"
              />
              <p className="text-xs text-muted-foreground">Primary email cannot be changed</p>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="mobile_number" className="flex items-center gap-2">
              <Phone size={14} />
              Mobile Number
            </Label>
            <Input
              id="mobile_number"
              data-testid="profile-mobile"
              value={profileData.mobile_number}
              onChange={(e) => setProfileData(prev => ({ ...prev, mobile_number: e.target.value }))}
              placeholder="+971 50 123 4567"
            />
          </div>
          
          <Button 
            onClick={handleSaveProfile} 
            disabled={loading}
            data-testid="save-profile-btn"
          >
            <Save size={16} className="mr-2" />
            {loading ? 'Saving...' : 'Save Changes'}
          </Button>
        </CardContent>
      </Card>

      {/* Additional Emails */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail size={20} />
            Additional Email Addresses
          </CardTitle>
          <CardDescription>Add alternative email addresses for your account</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Input
              data-testid="new-additional-email"
              value={newAdditionalEmail}
              onChange={(e) => setNewAdditionalEmail(e.target.value)}
              placeholder="Enter additional email address"
              onKeyDown={(e) => e.key === 'Enter' && handleAddEmail()}
            />
            <Button onClick={handleAddEmail} variant="outline" data-testid="add-email-btn">
              <Plus size={16} className="mr-2" />
              Add
            </Button>
          </div>
          
          {profileData.additional_emails.length > 0 ? (
            <div className="space-y-2">
              {profileData.additional_emails.map((email, index) => (
                <div 
                  key={index} 
                  className="flex items-center justify-between p-3 bg-muted rounded-lg"
                >
                  <div className="flex items-center gap-2">
                    <Mail size={14} className="text-muted-foreground" />
                    <span>{email}</span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveEmail(email)}
                    className="text-destructive hover:text-destructive"
                    data-testid={`remove-email-${index}`}
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No additional emails added</p>
          )}
          
          {profileData.additional_emails.length > 0 && (
            <Button 
              onClick={handleSaveProfile} 
              disabled={loading}
              variant="outline"
            >
              <Save size={16} className="mr-2" />
              Save Email Changes
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Change Password */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock size={20} />
            {hasPassword ? 'Change Password' : 'Set Password'}
          </CardTitle>
          <CardDescription>
            {hasPassword 
              ? 'Update your account password' 
              : 'Set a password to enable email/password login for your account'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Shape the form the way Chrome/Safari/1Password expect for a
              change-password flow so the browser can:
                1. Autofill the Current Password with the saved credential.
                2. Detect a successful submission and prompt the user to
                   update the saved credential to the new one.
              Requirements per web.dev/sign-in-form-best-practices:
                - A real <form> element.
                - A hidden username input matching the current user's email
                  with autoComplete="username", so the browser knows which
                  saved credential to update (not just "some random form").
                - autoComplete="current-password" on the old-password field.
                - autoComplete="new-password" on both new + confirm fields.
              handleChangePassword still runs on submit (Enter key or the
              button); we just prevent the native form action. */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleChangePassword();
            }}
            className="space-y-4"
          >
            <input
              type="text"
              name="username"
              autoComplete="username"
              value={user?.email || ''}
              readOnly
              hidden
              // Rendered but visually hidden so Chrome sees a username
              // context. Do not remove even though it looks unused.
            />
            {/* Only show current password field if user already has a password */}
            {hasPassword && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="current_password">Current Password</Label>
                  <div className="relative">
                    <Input
                      id="current_password"
                      data-testid="current-password"
                      type={showPasswords.current ? 'text' : 'password'}
                      value={passwordData.current_password}
                      onChange={(e) => setPasswordData(prev => ({ ...prev, current_password: e.target.value }))}
                      placeholder="Enter current password"
                      autoComplete="current-password"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-2 top-1/2 -translate-y-1/2"
                      onClick={() => togglePasswordVisibility('current')}
                    >
                      {showPasswords.current ? <EyeOff size={16} /> : <Eye size={16} />}
                    </Button>
                  </div>
                </div>
                <Separator />
              </>
            )}
          
          {!hasPassword && (
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg mb-4">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-5 w-5 text-amber-500 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-800">No password set</p>
                  <p className="text-sm text-amber-600">
                    Your account was created via Google login. Set a password below to also enable email/password login.
                  </p>
                </div>
              </div>
            </div>
          )}
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="new_password">New Password</Label>
              <div className="relative">
                <Input
                  id="new_password"
                  data-testid="new-password"
                  type={showPasswords.new ? 'text' : 'password'}
                  value={passwordData.new_password}
                  onChange={(e) => setPasswordData(prev => ({ ...prev, new_password: e.target.value }))}
                  placeholder="Enter new password"
                  autoComplete="new-password"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-2 top-1/2 -translate-y-1/2"
                  onClick={() => togglePasswordVisibility('new')}
                >
                  {showPasswords.new ? <EyeOff size={16} /> : <Eye size={16} />}
                </Button>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="confirm_password">Confirm New Password</Label>
              <div className="relative">
                <Input
                  id="confirm_password"
                  data-testid="confirm-password"
                  type={showPasswords.confirm ? 'text' : 'password'}
                  value={passwordData.confirm_password}
                  onChange={(e) => setPasswordData(prev => ({ ...prev, confirm_password: e.target.value }))}
                  placeholder="Confirm new password"
                  autoComplete="new-password"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-2 top-1/2 -translate-y-1/2"
                  onClick={() => togglePasswordVisibility('confirm')}
                >
                  {showPasswords.confirm ? <EyeOff size={16} /> : <Eye size={16} />}
                </Button>
              </div>
            </div>
          </div>
          
          <div className="text-sm text-muted-foreground space-y-1">
            <p className="flex items-center gap-1">
              {passwordData.new_password.length >= 8 ? (
                <CheckCircle size={14} className="text-green-500" />
              ) : (
                <AlertCircle size={14} className="text-muted-foreground" />
              )}
              At least 8 characters
            </p>
            <p className="flex items-center gap-1">
              {passwordData.new_password === passwordData.confirm_password && passwordData.new_password ? (
                <CheckCircle size={14} className="text-green-500" />
              ) : (
                <AlertCircle size={14} className="text-muted-foreground" />
              )}
              Passwords match
            </p>
          </div>
          
            <Button
              type="submit"
              disabled={passwordLoading || (hasPassword && !passwordData.current_password) || !passwordData.new_password || !passwordData.confirm_password}
              data-testid="change-password-btn"
            >
              <Lock size={16} className="mr-2" />
              {passwordLoading ? 'Changing...' : (hasPassword ? 'Change Password' : 'Set Password')}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Account Access */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield size={20} />
            Account Access
          </CardTitle>
          <CardDescription>Your current role and permissions</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
              <div>
                <p className="font-medium">Role</p>
                <p className="text-sm text-muted-foreground">Your assigned role in the system</p>
              </div>
              <Badge variant="secondary" className="text-sm">
                {user?.role?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'User'}
              </Badge>
            </div>
            
            {user?.permissions && user.permissions.length > 0 && (
              <div className="space-y-2">
                <p className="font-medium">Permissions</p>
                <div className="flex flex-wrap gap-2">
                  {user.permissions.map((permission, index) => (
                    <Badge key={index} variant="outline" className="text-xs">
                      {permission.replace(/_/g, ' ')}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            
            {(!user?.permissions || user.permissions.length === 0) && user?.role !== 'super_admin' && user?.role !== 'admin' && (
              <div className="p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                <p className="text-sm text-yellow-600 dark:text-yellow-400">
                  <AlertCircle size={14} className="inline mr-2" />
                  Your account has limited access. Contact your administrator to request additional permissions.
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProfileSettings;
